import { CorporateAdmins } from '@platform/client';
import { Icon } from '@platform/components';
import cx from 'classix';
import { useEffect, useRef, useState } from 'react';
import { CohortRow } from './CohortRow';

const RECENT_WINDOW_MONTHS = 3;

const isCohortInRecentWindow = (
  cohort: { startDate: string | null; endDate: string | null },
  now: Date,
): boolean => {
  const lower = new Date(now.getFullYear(), now.getMonth() - RECENT_WINDOW_MONTHS, now.getDate());
  const upper = new Date(now.getFullYear(), now.getMonth() + RECENT_WINDOW_MONTHS, now.getDate());
  const inRange = (dateStr: string | null) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return d >= lower && d <= upper;
  };
  return inRange(cohort.startDate) || inRange(cohort.endDate);
};

export const sortCohortsForDisplay = <T extends { name: string; startDate: string | null }>(
  cohorts: T[],
): T[] =>
  [...cohorts].sort((a, b) => {
    if (a.startDate && !b.startDate) return -1;
    if (!a.startDate && b.startDate) return 1;
    if (a.startDate && b.startDate && a.startDate !== b.startDate) {
      return a.startDate < b.startDate ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });

export const CohortsDropDown = ({
  selectedCohortId,
  onCohortSelect,
}: {
  selectedCohortId?: number;
  onCohortSelect: (cohortId: number) => void;
}) => {
  const { data: cohortsData } = CorporateAdmins.useGetClientCohorts();
  const cohorts = sortCohortsForDisplay(cohortsData?.cohorts ?? []);

  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedCohort = cohorts.find((c) => c.id === selectedCohortId);
  const now = new Date();
  const visibleCohorts = cohorts.filter((c) => isCohortInRecentWindow(c, now));
  const overflowCohorts = cohorts.filter((c) => !isCohortInRecentWindow(c, now));
  const hasOverflow = overflowCohorts.length > 0;
  const isDisabled = cohorts.length === 0;

  const handleSelect = (id: number) => {
    onCohortSelect(id);
    setIsOpen(false);
    setIsExpanded(false);
  };

  return (
    <div ref={containerRef} className='relative w-[340px]'>
      <button
        type='button'
        disabled={isDisabled}
        onClick={() => setIsOpen((v) => !v)}
        className={cx(
          'inline-flex w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-base shadow-xs outline-none',
          isOpen ? 'border-brand-300 ring-brand-shadow-xs' : 'border-gray-100',
          isDisabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className={cx('truncate', !selectedCohort && 'text-gray-400')}>
          {selectedCohort?.name ?? 'Select a cohort'}
        </span>
        <span className='ml-2 [&>svg>path]:stroke-gray-400'>
          <Icon type={isOpen ? 'chevron-up' : 'chevron-down'} size='20' />
        </span>
      </button>

      {isOpen && (
        <div className='absolute left-0 right-0 z-20 mt-[5px] max-h-[60vh] overflow-y-auto rounded-lg border border-gray-100 bg-white shadow-lg'>
          {visibleCohorts.map((cohort) => (
            <CohortRow
              key={cohort.id}
              cohort={cohort}
              isSelected={cohort.id === selectedCohortId}
              onSelect={handleSelect}
            />
          ))}
          {hasOverflow && (
            <>
              <button
                type='button'
                onClick={() => setIsExpanded((v) => !v)}
                className='flex w-full items-center justify-between border-t border-gray-100 px-4 py-3 text-base font-semibold text-gray-700 outline-none hover:bg-gray-50'
              >
                <span>{isExpanded ? 'See less' : 'See more'}</span>
                <span className='[&>svg>path]:stroke-gray-700'>
                  <Icon type={isExpanded ? 'chevron-up' : 'chevron-down'} size='20' />
                </span>
              </button>
              {isExpanded &&
                overflowCohorts.map((cohort) => (
                  <CohortRow
                    key={cohort.id}
                    cohort={cohort}
                    isSelected={cohort.id === selectedCohortId}
                    onSelect={handleSelect}
                  />
                ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
